import { useState, useEffect } from 'react';
import { useAccount, useBalance, useReadContract } from 'wagmi';
import { formatUnits } from 'viem';
import { CONTRACT_ADDRESSES } from '../config/contracts';
import { BACKEND_URL } from '../config/api';
import { TOKEN_LOGOS } from '../config/tokens';

const ERC20_ABI = [
  {
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
];

function TokenBalance({ token, address, logo }) {
  const { data: balance } = useReadContract({
    address: token.contract,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: (address && token.contract) ? [address] : undefined,
    query: {
      enabled: !!(address && token.contract),
      retry: false,
      throwOnError: false,
    },
  });

  // Use token decimals, but default to 18 if not set or if 0 (which is likely incorrect for ERC20 tokens)
  const tokenDecimals = (token.decimals && token.decimals > 0) ? token.decimals : 18;
  const formattedBalance = balance
    ? parseFloat(formatUnits(balance, tokenDecimals)).toFixed(4)
    : '0.0000';

  return (
    <div style={{
      backgroundColor: '#2a2a2a',
      border: '1px solid #3a3a3a',
      borderRadius: '12px',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      {logo ? (
        <img
          src={logo}
          alt={token.symbol}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            objectFit: 'cover',
            border: '2px solid #3a3a3a'
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: '#3a3a3a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.2rem',
          fontWeight: '700',
          color: '#A78BFA'
        }}>
          {token.symbol.charAt(0)}
        </div>
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: '700', color: '#A78BFA', fontSize: '1rem' }}>
          {token.symbol}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {token.name}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontWeight: '700', color: '#fff', fontSize: '1.1rem' }}>
          {formattedBalance}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
          {token.symbol}
        </div>
      </div>
    </div>
  );
}

const ADMIN_WALLET = '0xb87f3f42B15708c95465cd97fFaCd92B3dEBf216'.toLowerCase();

export default function Dashboard({ onNavigate }) {
  const { address, isConnected } = useAccount();
  const [wrappedTokens, setWrappedTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingTokens, setPendingTokens] = useState([]);
  const [allTokens, setAllTokens] = useState([]);
  const [adminAction, setAdminAction] = useState('');
  const [allocSuggestion, setAllocSuggestion] = useState({ points: 100, explanation: '' });
  const [liquidityPoolEvents, setLiquidityPoolEvents] = useState([]);

  // Native FLR balance
  const { data: flrBalance } = useBalance({
    address: address,
    query: { enabled: !!address },
  });

  useEffect(() => {
    loadWrappedTokens();
    if (address?.toLowerCase() === ADMIN_WALLET) {
      loadPendingTokens();
      loadAllTokens();
      loadLiquidityPoolEvents();
    }
  }, [address]);

  const loadWrappedTokens = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tokens/list?status=approved`);
      const data = await res.json();

      const wrapped = (data.tokens || [])
        .filter(t => t.flare_contract)
        .map(t => ({
          symbol: `w${t.symbol}`,
          name: `Wrapped ${t.name}`,
          contract: t.flare_contract,
          decimals: 18, // All wrapped tokens on Flare use 18 decimals
          imageUrl: t.image_url || '',
        }));

      setWrappedTokens(wrapped);
    } catch (e) {
      console.error('Error loading tokens:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingTokens = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/tokens/pending`);
      const data = await res.json();
      setPendingTokens(data.tokens || []);
    } catch (e) {
      console.error('Error loading pending tokens:', e);
    }
  };

  const loadAllTokens = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tokens/list`);
      const data = await res.json();
      setAllTokens(data.tokens || []);
    } catch (e) {
      console.error('Error loading all tokens:', e);
    }
  };

  const loadLiquidityPoolEvents = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/liquidity-pool-events/unnotified`);
      const data = await res.json();
      setLiquidityPoolEvents(data.events || []);
    } catch (e) {
      console.error('Error loading liquidity pool events:', e);
    }
  };

  const handleDismissLiquidityEvent = async (eventId) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/liquidity-pool-events/${eventId}/mark-notified`, {
        method: 'POST'
      });
      loadLiquidityPoolEvents();
    } catch (e) {
      console.error('Error dismissing liquidity pool event:', e);
    }
  };

  const fetchAllocSuggestion = async (symbol, pairToken) => {
    try {
      if (!symbol || !pairToken) return;
      const res = await fetch(`${BACKEND_URL}/api/admin/farm-pools/suggest-alloc-points?symbol=${encodeURIComponent(symbol)}&pair_token=${encodeURIComponent(pairToken)}`);
      const data = await res.json();
      if (data.suggested_alloc_points !== undefined) {
        setAllocSuggestion({
          points: data.suggested_alloc_points,
          explanation: data.explanation || ''
        });
        // Update the input field with suggested value
        const allocInput = document.getElementById('farm-alloc-points');
        if (allocInput) {
          allocInput.value = data.suggested_alloc_points;
        }
      }
    } catch (e) {
      console.error('Error fetching allocation suggestion:', e);
    }
  };

  const handleApprove = async (symbol) => {
    setAdminAction(`Approving ${symbol}...`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/tokens/${symbol}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: address }),
      });
      const data = await res.json();
      if (data.success) {
        setAdminAction(`✅ ${symbol} approved! Wrapped token: ${data.flare_contract?.substring(0, 10)}...`);
        loadPendingTokens();
        loadAllTokens();
        loadWrappedTokens();
      } else {
        setAdminAction(`❌ Error: ${data.detail || 'Approval failed'}`);
      }
    } catch (e) {
      setAdminAction(`❌ Error approving ${symbol}: ${e.message}`);
    }
  };

  const handleReject = async (symbol) => {
    const reason = prompt(`Enter rejection reason for ${symbol}:`);
    if (!reason) return;

    setAdminAction(`Rejecting ${symbol}...`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/tokens/${symbol}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (data.success) {
        setAdminAction(`✅ ${symbol} rejected`);
        loadPendingTokens();
        loadAllTokens();
      } else {
        setAdminAction(`❌ Error: ${data.detail || 'Rejection failed'}`);
      }
    } catch (e) {
      setAdminAction(`❌ Error rejecting ${symbol}: ${e.message}`);
    }
  };

  const handleDelete = async (symbol) => {
    if (!confirm(`Are you sure you want to delete ${symbol} from the registry?`)) return;

    setAdminAction(`Deleting ${symbol}...`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/tokens/${symbol}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.success) {
        setAdminAction(`✅ ${symbol} deleted`);
        loadPendingTokens();
        loadAllTokens();
        loadWrappedTokens();
      } else {
        setAdminAction(`❌ Error: ${data.detail || 'Deletion failed'}`);
      }
    } catch (e) {
      setAdminAction(`❌ Error deleting ${symbol}: ${e.message}`);
    }
  };

  const handleDeploy = async (symbol) => {
    setAdminAction(`Deploying wrapped token contract for ${symbol}...`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/tokens/${symbol}/deploy`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.success) {
        if (data.already_deployed) {
          setAdminAction(`ℹ️ ${symbol} already has a deployed contract: ${data.flare_contract?.substring(0, 10)}...`);
        } else {
          setAdminAction(`✅ ${symbol} wrapped token deployed! Contract: ${data.flare_contract?.substring(0, 10)}...`);
        }
        loadAllTokens();
        loadWrappedTokens();
      } else {
        setAdminAction(`❌ Error: ${data.detail || 'Deployment failed'}`);
      }
    } catch (e) {
      setAdminAction(`❌ Error deploying ${symbol}: ${e.message}`);
    }
  };

  const isAdmin = address?.toLowerCase() === ADMIN_WALLET;

  if (!isConnected) {
    return (
      <div className="content-section active">
        <div className="section" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>💼</div>
          <h2 style={{ color: '#A78BFA', marginBottom: '12px' }}>Dashboard</h2>
          <p style={{ color: '#9ca3af', marginBottom: '24px', lineHeight: '1.6' }}>
            Connect your Flare wallet to view your token balances
          </p>
        </div>
      </div>
    );
  }

  const flrFormatted = flrBalance
    ? parseFloat(formatUnits(flrBalance.value, 18)).toFixed(4)
    : '0.0000';

  return (
    <div className="content-section active">
      <div className="section">
        <h2 style={{ color: '#A78BFA', marginBottom: '8px' }}>Dashboard</h2>
        <p style={{ color: '#9ca3af', marginBottom: '24px', fontSize: '0.875rem' }}>
          Your Flare Network token balances
        </p>

        {/* Wallet Address */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #3a3a3a',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Connected Wallet:</span>
          <code style={{ color: '#FFB84D', fontSize: '0.75rem', fontFamily: 'monospace' }}>
            {address?.substring(0, 6)}...{address?.substring(38)}
          </code>
        </div>

        <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '12px', marginTop: '24px' }}>
          Native & Platform Tokens
        </h3>

        <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
          {/* FLR Balance */}
          <div style={{
            backgroundColor: '#2a2a2a',
            border: '1px solid #3a3a3a',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <img
              src={TOKEN_LOGOS['FLR']}
              alt="FLR"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #3a3a3a'
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '700', color: '#A78BFA', fontSize: '1rem' }}>
                FLR
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                Flare (native)
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '700', color: '#fff', fontSize: '1.1rem' }}>
                {flrFormatted}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                FLR
              </div>
            </div>
          </div>

          {/* EMBER Balance */}
          {CONTRACT_ADDRESSES.EMBER_TOKEN && (
            <TokenBalance
              token={{
                symbol: 'EMBER',
                name: 'EmberX Token',
                contract: CONTRACT_ADDRESSES.EMBER_TOKEN,
                decimals: 18
              }}
              address={address}
              logo={TOKEN_LOGOS['EMBER']}
            />
          )}

          {/* WFLR Balance */}
          {CONTRACT_ADDRESSES.WFLR && (
            <TokenBalance
              token={{
                symbol: 'WFLR',
                name: 'Wrapped Flare',
                contract: CONTRACT_ADDRESSES.WFLR,
                decimals: 18
              }}
              address={address}
              logo={TOKEN_LOGOS['WFLR']}
            />
          )}
        </div>

        {/* Wrapped Tokens */}
        {wrappedTokens.length > 0 && (
          <>
            <h3 style={{ color: '#fff', fontSize: '1rem', marginBottom: '12px', marginTop: '24px' }}>
              Wrapped XRPL Tokens
            </h3>
            <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
              {wrappedTokens.map(token => (
                <TokenBalance
                  key={token.contract}
                  token={token}
                  address={address}
                  logo={token.imageUrl}
                />
              ))}
            </div>
          </>
        )}

        {loading && (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>
            Loading tokens...
          </div>
        )}

        {/* Admin Panel - Only visible to dev wallet */}
        {isAdmin && (
          <>
            {/* Liquidity Pool Notifications */}
            {liquidityPoolEvents.length > 0 && (
              <div style={{
                backgroundColor: '#0f2e1a',
                border: '2px solid #10B981',
                borderRadius: '12px',
                padding: '20px',
                marginTop: '32px',
                marginBottom: '24px'
              }}>
                <h3 style={{ color: '#10B981', fontSize: '1.2rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🎉 New Liquidity Pools Created
                </h3>
                <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '16px' }}>
                  Registered tokens have created liquidity pools. You can now create farm pools for these tokens!
                </p>

                <div style={{ display: 'grid', gap: '12px' }}>
                  {liquidityPoolEvents.map(event => (
                    <div key={event.id} style={{
                      backgroundColor: '#1a2e22',
                      border: '1px solid #10B981',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#10B981', marginBottom: '8px' }}>
                            {event.token_symbol} Liquidity Pool Created! 💧
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: 'monospace', marginBottom: '4px' }}>
                            Pair Address: {event.pair_address?.substring(0, 16)}...
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: 'monospace', marginBottom: '4px' }}>
                            Token: {event.token_address?.substring(0, 16)}...
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '8px' }}>
                            Created: {new Date(event.created_at).toLocaleString()}
                          </div>
                          <div style={{
                            fontSize: '0.875rem',
                            color: '#10B981',
                            backgroundColor: '#0f2e1a',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            marginTop: '8px',
                            border: '1px solid #10B981'
                          }}>
                            💡 You can now create a farm pool for {event.token_symbol} in the "Create Farm Pool" section below!
                          </div>
                        </div>
                        <button
                          onClick={() => handleDismissLiquidityEvent(event.id)}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#6B7280',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            marginLeft: '16px'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#4B5563'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#6B7280'}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{
              backgroundColor: '#1a0f2e',
              border: '2px solid #8B5CF6',
              borderRadius: '12px',
              padding: '20px',
              marginTop: '32px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#8B5CF6', fontSize: '1.2rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🔑 Admin Panel
              </h3>
              <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '16px' }}>
                Manage pending token registrations
              </p>

              {adminAction && (
                <div style={{
                  backgroundColor: '#0f0520',
                  border: '1px solid #8B5CF6',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  color: '#C4B5FD',
                  fontSize: '0.875rem'
                }}>
                  {adminAction}
                </div>
              )}

              {pendingTokens.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>
                  No pending tokens
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {pendingTokens.map(token => (
                    <div key={token.symbol} style={{
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#FFB84D', marginBottom: '4px' }}>
                            {token.symbol} - {token.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: 'monospace' }}>
                            Issuer: {token.xrpl_issuer?.substring(0, 12)}...
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            Decimals: {token.decimals} | Paid: {token.payment_txid?.substring(0, 10)}...
                          </div>
                        </div>
                        {token.image_url && (
                          <img
                            src={token.image_url}
                            alt={token.symbol}
                            style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '2px solid #3a3a3a'
                            }}
                          />
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleApprove(token.symbol)}
                          style={{
                            flex: 1,
                            padding: '10px',
                            backgroundColor: '#10B981',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.875rem'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#10B981'}
                        >
                          ✅ Approve
                        </button>
                        <button
                          onClick={() => handleReject(token.symbol)}
                          style={{
                            flex: 1,
                            padding: '10px',
                            backgroundColor: '#EF4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.875rem'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#DC2626'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#EF4444'}
                        >
                          ❌ Reject
                        </button>
                        <button
                          onClick={() => handleDelete(token.symbol)}
                          style={{
                            padding: '10px 16px',
                            backgroundColor: '#6B7280',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.875rem'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#4B5563'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#6B7280'}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* All Tokens Management */}
            <div style={{
              backgroundColor: '#1a0f2e',
              border: '2px solid #8B5CF6',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#8B5CF6', fontSize: '1.2rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🗂️ All Registered Tokens
              </h3>
              <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '16px' }}>
                View and manage all tokens (approved, pending, rejected)
              </p>

              {allTokens.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>
                  No tokens registered yet
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {allTokens.map(token => (
                    <div key={token.symbol} style={{
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#FFB84D' }}>
                              {token.symbol}
                            </div>
                            <div style={{
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: token.status === 'approved' ? '#10B981' : token.status === 'pending' ? '#F59E0B' : '#EF4444',
                              color: '#fff'
                            }}>
                              {token.status}
                            </div>
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: 'monospace' }}>
                            Issuer: {token.xrpl_issuer?.substring(0, 12)}...
                          </div>
                          {token.flare_contract && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: 'monospace' }}>
                              Contract: {token.flare_contract?.substring(0, 12)}...
                            </div>
                          )}
                          {token.status === 'approved' && !token.flare_contract && (
                            <div style={{ fontSize: '0.75rem', color: '#F59E0B', marginTop: '4px' }}>
                              ⚠️ No wrapped token contract deployed
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                          {token.status === 'approved' && !token.flare_contract && (
                            <button
                              onClick={() => handleDeploy(token.symbol)}
                              style={{
                                padding: '10px 20px',
                                backgroundColor: '#10B981',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '0.875rem'
                              }}
                              onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                              onMouseOut={(e) => e.target.style.backgroundColor = '#10B981'}
                            >
                              🚀 Deploy Contract
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(token.symbol)}
                            style={{
                              padding: '10px 20px',
                              backgroundColor: '#EF4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '0.875rem'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#DC2626'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#EF4444'}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Farm Pool Creation */}
            <div style={{
              backgroundColor: '#1a0f2e',
              border: '2px solid #10B981',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#10B981', fontSize: '1.2rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🌾 Create Farm Pool
              </h3>
              <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '16px' }}>
                Create a new farm pool for an approved token. Liquidity must exist before creating the farm.
              </p>

              <div style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ color: '#9ca3af', fontSize: '0.875rem', display: 'block', marginBottom: '8px' }}>
                    Token Symbol
                  </label>
                  <select
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '0.875rem'
                    }}
                    onChange={(e) => {
                      const farmSymbol = e.target.value;
                      document.getElementById('farm-symbol-input').value = farmSymbol;
                      // Fetch allocation suggestion when token changes
                      const pairToken = document.getElementById('farm-pair-token').value || 'WFLR';
                      if (farmSymbol) {
                        fetchAllocSuggestion(farmSymbol, pairToken);
                      }
                    }}
                  >
                    <option value="">Select token...</option>
                    {/* Platform tokens */}
                    {CONTRACT_ADDRESSES.EMBER_TOKEN && (
                      <option value="EMBER">EMBER</option>
                    )}
                    {CONTRACT_ADDRESSES.WFLR && (
                      <option value="WFLR">WFLR</option>
                    )}
                    {/* Backend registered tokens (XRPL-bridged tokens shown with w prefix) */}
                    {allTokens.filter(t => t.status === 'approved' && t.flare_contract).map(token => (
                      <option key={token.symbol} value={token.symbol}>w{token.symbol}</option>
                    ))}
                  </select>
                  <input
                    type="hidden"
                    id="farm-symbol-input"
                  />
                </div>

                <div>
                  <label style={{ color: '#9ca3af', fontSize: '0.875rem', display: 'block', marginBottom: '8px' }}>
                    Pair With
                  </label>
                  <input
                    type="text"
                    id="farm-pair-token"
                    defaultValue="WFLR"
                    onChange={(e) => {
                      // Fetch allocation suggestion when pair changes
                      const symbol = document.getElementById('farm-symbol-input').value;
                      if (symbol) {
                        fetchAllocSuggestion(symbol, e.target.value);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '0.875rem'
                    }}
                    placeholder="e.g., WFLR"
                  />
                </div>

                <div>
                  <label style={{ color: '#9ca3af', fontSize: '0.875rem', display: 'block', marginBottom: '8px' }}>
                    Allocation Points (Higher = More EMBER rewards)
                  </label>
                  <input
                    type="number"
                    id="farm-alloc-points"
                    defaultValue="100"
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '0.875rem'
                    }}
                    placeholder="e.g., 100"
                  />
                  {allocSuggestion.explanation && (
                    <div style={{
                      marginTop: '8px',
                      padding: '10px',
                      backgroundColor: '#10B98115',
                      border: '1px solid #10B98150',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      color: '#10B981'
                    }}>
                      💡 Suggested: <strong>{allocSuggestion.points}</strong> points - {allocSuggestion.explanation}
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={async () => {
                  const symbol = document.getElementById('farm-symbol-input').value;
                  const pairToken = document.getElementById('farm-pair-token').value;
                  const allocPoints = parseInt(document.getElementById('farm-alloc-points').value);

                  if (!symbol) {
                    setAdminAction('❌ Please select a token');
                    return;
                  }

                  setAdminAction(`Creating farm pool for ${symbol}/${pairToken}...`);

                  try {
                    const res = await fetch(`${BACKEND_URL}/api/admin/farm-pools/create`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        symbol,
                        pair_token: pairToken,
                        alloc_points: allocPoints
                      })
                    });
                    const data = await res.json();
                    if (data.success) {
                      setAdminAction(`✅ Farm pool created! Pool ID: ${data.pool_id}`);
                      loadAllTokens();
                    } else {
                      setAdminAction(`❌ Error: ${data.detail || 'Farm creation failed'}`);
                    }
                  } catch (e) {
                    setAdminAction(`❌ Error: ${e.message}`);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#10B981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#10B981'}
              >
                🌾 Create Farm Pool
              </button>

              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#0f0520', borderRadius: '6px' }}>
                <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>
                  ⚠️ Make sure liquidity exists for the pair before creating the farm. Users need to add liquidity first via the Liquidity section.
                </p>
              </div>
            </div>
          </>
        )}

        {/* Info Box */}
        <div style={{
          backgroundColor: '#0f0520',
          border: '1px solid rgba(109, 40, 217, 0.5)',
          borderRadius: '8px',
          padding: '12px',
          marginTop: '24px'
        }}>
          <p style={{ color: '#C4B5FD', fontSize: '0.875rem', lineHeight: '1.6', margin: 0 }}>
            💡 This dashboard shows your Flare Network balances. Use the{' '}
            <span
              onClick={() => onNavigate && onNavigate('bridge')}
              style={{ color: '#FFB84D', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Bridge
            </span>
            {' '}to wrap XRPL tokens to Flare, or{' '}
            <span
              onClick={() => onNavigate && onNavigate('swap')}
              style={{ color: '#FFB84D', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Swap
            </span>
            {' '}to trade tokens.
          </p>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '48px',
          paddingTop: '24px',
          borderTop: '1px solid #3a3a3a',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '0.875rem'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <span
              onClick={() => onNavigate && onNavigate('terms')}
              style={{
                color: '#A78BFA',
                textDecoration: 'none',
                marginRight: '24px',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              Terms & Conditions
            </span>
            <span
              onClick={() => onNavigate && onNavigate('privacy')}
              style={{
                color: '#A78BFA',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              Privacy Policy
            </span>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <span style={{ fontWeight: '600', color: '#fff' }}>Contact Us: </span>
            <a
              href="mailto:support@EmberX.xyz"
              style={{
                color: '#FFB84D',
                textDecoration: 'none',
                marginRight: '16px'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              ✉️ support@EmberX.xyz
            </a>
            <span style={{ marginRight: '8px' }}>|</span>
            <a
              href="https://x.com/EmberXFi"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#FFB84D',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '2px' }}>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              @EmberXFi
            </a>
          </div>
          <p style={{ margin: '0' }}>
            © 2025 EmberX. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
